import { useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  getCart,
  addToCart,
  updateCartItem,
  removeCartItem,
  clearCart,
  setCartPostcode,
  mergeCartLines as mergeCartLinesRequest,
  type AddToCartRequest,
  type MergeCartLineRequest,
  type UpdateCartItemRequest,
  type SetPostcodeRequest,
  type CartResponse,
  type CartItemResponse,
} from '../services/cart-api'
import {
  toAmount,
  trackBasketCleared,
  trackBasketItemAdded,
  trackBasketItemRemoved,
  trackBasketQuantityUpdated,
  trackProductAddedToBasketMode,
} from '../../lib/analytics'
import { perUnitPrice } from '../../utils/pricing'
import { clearAllCheckoutIds, pruneCheckoutIds } from '../../lib/checkout-session'
import type { CartItem } from '../../types'
import type { ProductAddMode } from '../../constants/quickAdd'

/**
 * Variables for the add-to-cart mutation.
 *
 * `inStockAtBranch` is analytics-only: it is stripped before the API call, and
 * exists because live stock is known on the product pages but not on the cart.
 * `productAddMode` is analytics-only too: set by quick add and the product
 * pages, it sends the product-added-mode event once the add succeeds.
 */
type AddToCartVariables = AddToCartRequest & {
  inStockAtBranch?: boolean
  productAddMode?: {
    mode: ProductAddMode
    // Quick add only: the card's place among its supplier's cards.
    productPosition: number | null
  }
}

/**
 * Convert backend CartItemResponse to frontend CartItem
 */
function convertToCartItem(item: CartItemResponse): CartItem {
  return {
    id: item.id,  // Backend cart item ID (UUID)
    productId: item.product_id,
    branchId: item.branch_id,
    supplierId: item.supplier_id,
    productName: item.product_name,
    supplierName: item.supplier_name,
    branchName: item.branch_name,
    branchIsActive: item.branch_is_active ?? true,  // Default to true if not provided
    branchAddressLine1: item.branch_address_line1,
    branchAddressLine2: item.branch_address_line2,
    branchCity: item.branch_city,
    branchPostcode: item.branch_postcode,
    branchPhone: item.branch_phone,
    supplierProductCode: item.supplier_product_code,
    price: parseFloat(String(item.price_at_added)),  // Convert to number
    priceAtAdded: parseFloat(String(item.price_at_added)),  // Original price
    quantity: item.quantity,
    quantityMultiple: item.quantity_multiple ?? 1,
    maxStock: 999, // TODO: Get from live stock API
    unitOfMeasure: item.unit_of_measure || 'Each',
    distanceMiles: item.distance_miles,
    masterProductId: item.master_product_id ? String(item.master_product_id) : null,
    imageUrl: item.image_url || undefined,
    fulfillmentOptions: [], // TODO: Get from live stock API
    isPreferred: item.is_preferred,
    isRestricted: item.is_restricted,
    isVanStock: item.is_van_stock,
    isVanStockToggled: item.is_van_stock_toggled,
    // Persisted borrowed delivery source — seeded from the last live-stock
    // sync. Cart groups by this on the first render; live stock later
    // replaces it via cartWithStock (and self-heals the DB if it disagrees).
    // Distance is postcode-dependent, not persisted — it comes from live
    // stock at render time (same pattern the own-branch distance follows).
    deliverySourceBranchId: item.delivery_source_branch_id ?? null,
    deliverySourceBranchName: item.delivery_source_branch_name ?? null,
    // Backend truth for the engineer's pickup/delivery pick. Cart.tsx reads
    // this first, then falls back to the sessionStorage bridge for lines
    // added before this field shipped.
    fulfillmentMethod: item.fulfillment_method ?? null,
    preferredDate: {
      supported: item.supports_preferred_date === true,
      maxWorkingDaysAfterOrder: item.preferred_date_max_working_days ?? null,
    },
  }
}

/**
 * Report a cart line's quantity change (basket_quantity_updated) with its
 * money impact. Shared by the update mutation and a quick add that lands on
 * top of an existing line — both are quantity changes, not new products.
 */
function trackLineQuantityChange(
  updatedItem: CartItemResponse,
  quantityBefore: number,
): void {
  // Money impact of the change. price_at_added is quoted per sell-unit
  // (e.g. per pack of 10) while quantities count base units, so the shared
  // per-unit rule applies before multiplying — the same rule the cart and
  // checkout totals use.
  const unitPrice = perUnitPrice(
    parseFloat(String(updatedItem.price_at_added)),
    updatedItem.quantity_multiple,
  )
  const previousValue = Number.isFinite(unitPrice)
    ? toAmount(unitPrice * quantityBefore)
    : null
  const newValue = Number.isFinite(unitPrice)
    ? toAmount(unitPrice * updatedItem.quantity)
    : null

  trackBasketQuantityUpdated({
    rawProductId: String(updatedItem.product_id),
    rawProductName: updatedItem.product_name,
    supplier: updatedItem.supplier_name,
    supplierId: String(updatedItem.supplier_id),
    branchId: String(updatedItem.branch_id),
    branchName: updatedItem.branch_name,
    previousQuantity: quantityBefore,
    newQuantity: updatedItem.quantity,
    delta: updatedItem.quantity - quantityBefore,
    previousValue,
    newValue,
    valueDelta:
      previousValue != null && newValue != null
        ? toAmount(newValue - previousValue)
        : null,
  })
}

/**
 * Hook to manage cart state with backend sync
 */
export function useCart() {
  const queryClient = useQueryClient()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()

  // Include tenant slug in query key to prevent cross-tenant cache bleed
  const cartQueryKey = ['cart', tenantSlug || 'default']

  // Fetch cart from backend. staleTime: Infinity means the cart is fetched
  // once per page load and never auto-refetched — mutations keep the cache
  // in sync via optimistic updates + response reconciliation, so there's no
  // need to re-GET the whole cart on every change. An explicit add (from
  // Search / ProductDetail) still invalidates to pull the new server item.
  const { data: cartData, isLoading, error } = useQuery<CartResponse>({
    queryKey: cartQueryKey,
    queryFn: getCart,
    staleTime: Infinity,
    refetchOnWindowFocus: false, // Don't refetch on tab switch
  })

  // Cross-tab freshness: when this tab regains focus, invalidate the cart so
  // a change made in another tab (fulfillment flip, add/remove, quantity)
  // lands here instead of persisting stale until reload. `staleTime: Infinity`
  // + `refetchOnWindowFocus: false` would otherwise ignore focus entirely;
  // invalidateQueries triggers a single refetch and dedupes across the N
  // useCart consumers mounted at once. `visibilitychange` covers both tab
  // switches and coming back from a minimized window — `focus` misses the
  // latter on some browsers.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const key = ['cart', tenantSlug || 'default']
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: key })
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [queryClient, tenantSlug])

  // Convert backend cart items to frontend format - MEMOIZED to prevent infinite re-renders
  // Without useMemo, this creates a new array reference on every render,
  // which causes useEffect dependencies to trigger infinitely
  const cart: CartItem[] = useMemo(
    () => cartData?.items.map(convertToCartItem) || [],
    [cartData?.items]
  )
  const postcode = cartData?.postcode || ''

  // Analytics: a checkout_id belongs to one branch group's journey through
  // checkout, so it must not outlive that group — otherwise a later basket
  // for the same branch reuses the abandoned checkout's id and scores it
  // converted. Emptying the whole basket voids every group; emptying a single
  // branch (while other branches still hold items) voids just that one, which
  // is why this prunes per branch rather than only clearing on an empty cart.
  // Covers every route to empty: Clear Cart, removing the last line of a
  // group, and the backend clearing items after a successful order.
  // Guarded on cartData so the empty array returned while the query is still
  // loading never clears a live checkout's id mid-flow. Pickup scopes are keyed
  // by each line's own branch, so they are pruned by branch. Delivery scopes are
  // keyed by the delivering (possibly borrowed) branch, which this hook cannot
  // see — so pruneCheckoutIds judges those by contents instead, using the same
  // per-line key (id, else `productId:branchId`) the signature is built from.
  useEffect(() => {
    if (isLoading || !cartData) return
    if (cart.length === 0) {
      clearAllCheckoutIds()
      return
    }
    pruneCheckoutIds(
      new Set(cart.map((item) => String(item.branchId))),
      new Set(
        cart.map((item) => item.id ?? `${item.productId}:${item.branchId}`),
      ),
    )
  }, [isLoading, cartData, cart])

  // Add to cart mutation
  const addMutation = useMutation({
    // The request is rebuilt field by field so inStockAtBranch — which is
    // analytics-only, and the one property the server response cannot supply
    // because live stock is known at the call site — never reaches the API.
    mutationFn: (variables: AddToCartVariables) =>
      addToCart({
        product_id: variables.product_id,
        branch_id: variables.branch_id,
        quantity: variables.quantity,
        // Forwarded when the caller passed it (ProductDetail always does);
        // omitting the key keeps the payload byte-identical for callers
        // that haven't been migrated yet.
        ...(variables.fulfillment_method
          ? { fulfillment_method: variables.fulfillment_method }
          : {}),
        // Quick add only; every other caller's payload stays unchanged.
        ...(variables.add_to_existing ? { add_to_existing: true } : {}),
      }),
    onSuccess: (createdItem, variables) => {
      // Warm path: cache is already populated → upsert the server's
      // authoritative item directly. No invalidate/refetch, because
      // invalidate-then-refetch was racing the Change Item flow's
      // add-then-remove: the remove's onMutate calls cancelQueries and
      // kills the queued refetch, so the new item never lands and the
      // cart briefly renders as empty (or missing the changed line).
      // Every other mutation reconciles via setQueryData for the same
      // reason (see useCart:83-91).
      // Cold path: the initial cart GET hasn't resolved yet (or errored)
      // — old is undefined. Can't safely seed a synthetic CartResponse
      // because a late-arriving pre-add GET could still overwrite it;
      // cancel any in-flight fetch and schedule a deferred refetch to
      // pull the authoritative snapshot instead. staleTime: Infinity
      // means without this the new item would be missing until reload.
      // Upsert semantics — the POST endpoint merges by (product, branch),
      // so a returning id may already be in the cache.
      const existingCache = queryClient.getQueryData<CartResponse>(cartQueryKey)
      // What the line held before a quick add landed on top of it, for
      // analytics below. Only an add-on-top can hit an existing line; null
      // when the product wasn't in the basket at this branch.
      const lineBeforeAddOnTop = variables.add_to_existing
        ? (existingCache?.items ?? []).find((item) => item.id === createdItem.id)
        : undefined
      const quantityBeforeAddOnTop = lineBeforeAddOnTop?.quantity ?? null
      if (existingCache) {
        queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
          if (!old) return old
          const existingIndex = old.items.findIndex((item) => item.id === createdItem.id)
          if (existingIndex >= 0) {
            const nextItems = [...old.items]
            nextItems[existingIndex] = createdItem
            return { ...old, items: nextItems }
          }
          return { ...old, items: [...old.items, createdItem] }
        })
      } else {
        queryClient.cancelQueries({ queryKey: cartQueryKey })
        queueMicrotask(() => {
          queryClient.refetchQueries({ queryKey: cartQueryKey })
        })
      }
      // Dispatch event for cart badge update
      window.dispatchEvent(new CustomEvent('cartUpdated'))

      // Analytics: how the product was added (quick add or a product page),
      // when the caller says. Sent here rather than from the caller's own
      // callback so it still fires if the engineer leaves the page mid-add.
      if (variables.productAddMode) {
        trackProductAddedToBasketMode({
          mode: variables.productAddMode.mode,
          rawProductId: String(createdItem.product_id),
          rawProductName: createdItem.product_name,
          supplier: createdItem.supplier_name,
          supplierId: String(createdItem.supplier_id),
          branchId: String(createdItem.branch_id),
          branchName: createdItem.branch_name,
          quantityAdded: variables.quantity,
          productPosition: variables.productAddMode.productPosition,
        })
      }

      // Analytics: a quick add on top of a line already in the basket is a
      // quantity change, not a new product.
      if (quantityBeforeAddOnTop !== null) {
        if (quantityBeforeAddOnTop !== createdItem.quantity) {
          trackLineQuantityChange(createdItem, quantityBeforeAddOnTop)
        }
        return
      }

      // Analytics: a NEW product entered the basket. Callers reach this
      // mutation only for a product the cart does not already hold — an
      // existing line goes through updateCartLine instead, whether the change
      // is quantity, branch, or both. So this cannot fire for a quantity
      // change, which is what the event requires.
      trackBasketItemAdded({
        rawProductId: String(createdItem.product_id),
        rawProductName: createdItem.product_name,
        supplier: createdItem.supplier_name,
        supplierId: String(createdItem.supplier_id),
        branchId: String(createdItem.branch_id),
        branchName: createdItem.branch_name,
        quantity: createdItem.quantity,
        // The cart's working postcode — the same value create_order persists
        // as stock_lookup_postcode, so it matches the other events.
        postcode: cartData?.postcode || null,
        isVanStock: createdItem.is_van_stock,
        inStockAtBranch: variables.inStockAtBranch ?? null,
      })
    },
    onError: () => {
      // Refetch on error to ensure the cache reflects whatever the server
      // ended up in. Deferred to a microtask so a subsequent mutation's
      // onMutate cancelQueries (e.g. the follow-up remove in Change Item's
      // add-then-remove) can't cancel this reconciliation before it fetches.
      queueMicrotask(() => {
        queryClient.refetchQueries({ queryKey: cartQueryKey })
      })
    },
  })

  // Update cart item mutation with optimistic updates
  const updateMutation = useMutation({
    mutationFn: ({
      itemId,
      data,
    }: {
      itemId: string
      data: UpdateCartItemRequest
      /**
       * Analytics only — the quantity before this change, supplied by callers
       * that write the cache optimistically before mutating. Without it the
       * onMutate snapshot below already holds the NEW value and the change
       * looks like a no-op.
       */
      previousQuantity?: number
    }) => updateCartItem(itemId, data),
    onMutate: async ({ itemId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: cartQueryKey })

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>(cartQueryKey)

      // Optimistically update the cache
      if (previousCart) {
        queryClient.setQueryData<CartResponse>(cartQueryKey, {
          ...previousCart,
          items: previousCart.items.map(item =>
            item.id === itemId ? { ...item, ...data } : item
          ),
        })
      }

      return { previousCart }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart)
      }
    },
    onSuccess: (updatedItem, { itemId, data, previousQuantity }, context) => {
      // Reconcile the cache with the server's authoritative item — no
      // full-cart refetch. This keeps the cache in sync with the DB while
      // avoiding the refetch that was racing the optimistic update and
      // snapping the quantity back.
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old
        return {
          ...old,
          items: old.items.map(item =>
            item.id === itemId ? { ...item, ...updatedItem } : item
          ),
        }
      })
      window.dispatchEvent(new CustomEvent('cartUpdated'))

      // Analytics: only a genuine quantity change. This mutation also carries
      // van-stock toggles and branch moves, and a branch move can be sent
      // alongside an unchanged quantity — so both the payload and the actual
      // before/after are checked. First adds and removals use their own
      // mutations, so neither can reach this.
      if (data.quantity == null) return
      // Prefer the caller-supplied value: the cart page writes the new
      // quantity into the cache for instant UI and only then debounces the
      // mutation, so by the time onMutate snapshots, the "previous" value is
      // already the new one. Falls back to the snapshot for callers that
      // mutate directly (the product pages).
      const quantityBefore =
        previousQuantity ??
        context?.previousCart?.items.find((item) => item.id === itemId)?.quantity
      if (quantityBefore == null || quantityBefore === updatedItem.quantity) {
        return
      }
      trackLineQuantityChange(updatedItem, quantityBefore)
    },
  })

  // Remove cart item mutation with optimistic updates
  const removeMutation = useMutation({
    mutationFn: (itemId: string) => removeCartItem(itemId),
    onMutate: async (itemId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: cartQueryKey })

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>(cartQueryKey)

      // Optimistically remove from cache
      if (previousCart) {
        queryClient.setQueryData<CartResponse>(cartQueryKey, {
          ...previousCart,
          items: previousCart.items.filter(item => item.id !== itemId),
        })
      }

      return { previousCart }
    },
    onError: (_err, _itemId, context) => {
      // Rollback on error
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart)
      }
    },
    onSuccess: (_data, itemId, context) => {
      // Item already removed from the cache optimistically and from the DB
      // by the mutation — nothing to reconcile, just notify the cart badge.
      // No refetch (avoids the full-cart reload on remove).
      window.dispatchEvent(new CustomEvent('cartUpdated'))

      // Analytics: the line left the basket entirely. A quantity decrease
      // goes through the update mutation instead, so it cannot reach here.
      // The delete response carries no body, so the removed line's details
      // come from the pre-removal snapshot onMutate already takes.
      const removedItem = context?.previousCart?.items.find(
        (item) => item.id === itemId,
      )
      if (!removedItem) return

      trackBasketItemRemoved({
        rawProductId: String(removedItem.product_id),
        rawProductName: removedItem.product_name,
        supplier: removedItem.supplier_name,
        supplierId: String(removedItem.supplier_id),
        quantityRemoved: removedItem.quantity,
      })
    },
  })

  // Clear cart mutation.
  //
  // `trackClear` marks the deliberate "Clear Cart" action so basket_cleared
  // fires only for it. Callers that omit the flag emit nothing. Since
  // DEV-51046 the only caller is the Clear Cart button — changing location
  // keeps the basket and no longer clears it.
  const clearMutation = useMutation({
    mutationFn: (_options?: { trackClear?: boolean }) => clearCart(),
    onMutate: (options) => {
      // Snapshot the basket BEFORE it is wiped so the value/count survive into
      // onSuccess (the cache is emptied by then). We only emit after the DELETE
      // succeeds, so a failed clear records nothing.
      if (!options?.trackClear) return
      const previousCart = queryClient.getQueryData<CartResponse>(cartQueryKey)
      const items = previousCart?.items ?? []
      if (items.length === 0) return
      // Per-unit rule so pack products (price quoted per pack) are not counted
      // at 10x — the same rounding the cart and quantity events use.
      const total = items.reduce((sum, item) => {
        const unitPrice = perUnitPrice(
          parseFloat(String(item.price_at_added)),
          item.quantity_multiple,
        )
        return Number.isFinite(unitPrice) ? sum + unitPrice * item.quantity : sum
      }, 0)
      return {
        clearedSnapshot: {
          itemCount: items.length,
          basketValue: toAmount(total),
        },
      }
    },
    onSuccess: (_data, _options, context) => {
      queryClient.invalidateQueries({ queryKey: cartQueryKey })
      window.dispatchEvent(new CustomEvent('cartUpdated'))
      if (context?.clearedSnapshot) {
        trackBasketCleared(context.clearedSnapshot)
      }
    },
  })

  // Merge lines of one product delivered from the same branch into the
  // oldest one (the basket asks for it). The kept line takes the summed
  // quantity; the others leave the cache. No analytics: nothing the engineer
  // ordered changed, only how it is held.
  const mergeLinesMutation = useMutation({
    mutationFn: (lines: MergeCartLineRequest[]) => mergeCartLinesRequest(lines),
    onSuccess: async ({ item, removed_item_ids: removedItemIds }) => {
      // A cart load already in flight would bring the deleted lines back.
      await queryClient.cancelQueries({ queryKey: cartQueryKey })
      const removedIds = new Set(removedItemIds)
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old
        return {
          ...old,
          items: old.items
            .filter((row) => !removedIds.has(row.id))
            .map((row) => (row.id === item.id ? item : row)),
        }
      })
      window.dispatchEvent(new CustomEvent('cartUpdated'))
    },
  })

  // Set postcode mutation
  const setPostcodeMutation = useMutation({
    mutationFn: (data: SetPostcodeRequest) => setCartPostcode(data),
    onSuccess: async () => {
      // Invalidate cart cache
      queryClient.invalidateQueries({ queryKey: cartQueryKey })

      // CRITICAL: Refetch 'auth-me' query to update User.last_postcode cache
      // This prevents cross-device sync from detecting false mismatches
      // Use refetchQueries (not invalidateQueries) to wait for completion
      // NOTE: Query key must match useMe.ts which uses ['auth-me']
      await queryClient.refetchQueries({ queryKey: ['auth-me'] })

      window.dispatchEvent(new CustomEvent('cartUpdated'))
    },
  })

  return {
    // Cart data
    cart,
    postcode,
    cartData,
    isLoading,
    error,

    // Cart operations
    addToCart: addMutation.mutate,
    // Async version — BM-015 Change Item needs the new cart line id after
    // a supplier/branch swap so it can attach the per-item fulfillment
    // override to it. mutateAsync resolves with the CartItemResponse the
    // server returns from the POST.
    addToCartAsync: addMutation.mutateAsync,
    updateCartItem: (itemId: string, quantity: number, previousQuantity?: number) =>
      updateMutation.mutate({ itemId, data: { quantity }, previousQuantity }),
    updateVanStockToggle: (itemId: string, isVanStockToggled: boolean | null) =>
      updateMutation.mutate({ itemId, data: { is_van_stock_toggled: isVanStockToggled } }),
    // Update a cart line's branch and/or quantity in one call. Async so callers
    // can await and surface the block-and-warn conflict (409) when moving the
    // product to a branch that already holds it. Optional fulfillment method
    // rides along so Change Item / Manage Order can persist the engineer's
    // pickup/delivery flip in the same round-trip.
    updateCartLine: (
      itemId: string,
      data: {
        branchId?: string
        quantity?: number
        fulfillmentMethod?: 'pickup' | 'delivery'
      },
    ) =>
      updateMutation.mutateAsync({
        itemId,
        data: {
          ...(data.branchId ? { branch_id: data.branchId } : {}),
          ...(data.quantity != null ? { quantity: data.quantity } : {}),
          ...(data.fulfillmentMethod
            ? { fulfillment_method: data.fulfillmentMethod }
            : {}),
        },
      }),
    removeCartItem: removeMutation.mutate,
    // Async version — BM-015 Change Item swaps a supplier/branch by
    // remove-then-add and must await the delete so the add doesn't race
    // the invalidation.
    removeCartItemAsync: removeMutation.mutateAsync,
    // Pass { trackClear: true } from the explicit Clear Cart button so
    // basket_cleared fires. mutateAsync so callers can await (Cart.tsx).
    clearCart: (options?: { trackClear?: boolean }) =>
      clearMutation.mutateAsync(options),
    setPostcode: setPostcodeMutation.mutateAsync, // Use mutateAsync for await in JobSetup.tsx
    // Async so the caller can toast on success and leave the lines alone on
    // failure.
    mergeCartLines: mergeLinesMutation.mutateAsync,

    // Mutation states
    isAdding: addMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRemoving: removeMutation.isPending,
    isClearing: clearMutation.isPending,
    isSettingPostcode: setPostcodeMutation.isPending,
  }
}
